import {Injectable} from '@angular/core';
import {BehaviorSubject, defer, EMPTY, Observable, of} from 'rxjs';
import {filter, map, repeatWhen, share, switchMap, switchMapTo, take, tap} from 'rxjs/operators';
import {bindWith} from '../../common/rxjs/bind-with';
import {dependOn} from '../../common/rxjs/depend-on';
import {AuthService} from '../auth/auth.service';
import {StreamEvent, StreamEventType, StreamNotReadyEvent, StreamReadyEvent} from './stream-event';
import {GenericStreamingService, WebSocketCommand, WebSocketMessage} from './streaming-interfaces';
import {StreamingService} from './streaming.service';

enum AuthWebSocketOperation {
    LOGIN = 'LOGIN'
}

enum AuthWebSocketMessageType {
    LOGIN_SUCCESS      = 'LOGIN_SUCCESS',
    LOGIN_SERVER_ERROR = 'LOGIN_SERVER_ERROR'
}

interface AuthLoginCommand extends WebSocketCommand {
    operation: AuthWebSocketOperation.LOGIN;
    data: [string]; // auth token (session ID)
}

@Injectable()
export class AuthStreamingService implements GenericStreamingService {

    public message$: Observable<WebSocketMessage>;
    public ready$: Observable<StreamReadyEvent>;
    public notReady$: Observable<StreamNotReadyEvent>;

    private isAuthorized: boolean = false;
    private readonly authStateSubject$ = new BehaviorSubject<boolean>(false);
    private readonly login$: Observable<StreamEvent> = this.login();
    private readonly authMessage$: Observable<WebSocketMessage>;
    private readonly notAuthMessage$: Observable<WebSocketMessage>;
    private readonly authorization$: Observable<StreamEvent>;

    constructor(
        protected streamingService: StreamingService,
        protected authService: AuthService
    ) {
        this.authStateSubject$.subscribe(isAuthorized => this.isAuthorized = isAuthorized);

        /**
         * Listen to core stream not-ready status regardless of subscription to auth-stream.
         * This resets auth state even when:
         * authorized -> unsubscribed from auth-stream -> reconnected -> subscribed again to auth-stream.
         */
        this.streamingService.passiveNotReady$
            .subscribe(() => this.authStateSubject$.next(false));

        this.authorization$ = this.login$.pipe(
            repeatWhen(next$ => next$.pipe(
                switchMapTo(this.streamingService.notReady$)
            )),
            share()
        );

        const isAuthMessage = (message: WebSocketMessage) => message.messageType in AuthWebSocketMessageType;
        this.authMessage$ = this.streamingService.message$.pipe(
            filter(isAuthMessage),
            share()
        );
        this.notAuthMessage$ = this.streamingService.message$.pipe(
            filter(message => !isAuthMessage(message)),
            share()
        );

        this.message$ = this.notAuthMessage$.pipe(
            dependOn(this.authorization$)
        );
        this.ready$ = this.authStateSubject$.pipe(
            filter(isAuthorized => isAuthorized),
            map(() => ({type: StreamEventType.READY} as StreamReadyEvent)),
            bindWith(this.authorization$)
        );
        this.notReady$ = this.authStateSubject$.pipe(
            filter(isAuthorized => !isAuthorized),
            map(() => ({type: StreamEventType.NOT_READY} as StreamNotReadyEvent)),
            bindWith(this.authorization$)
        );
    }

    public sendCommand(command: WebSocketCommand): void {
        return this.streamingService.sendCommand(command);
    }

    protected login(): Observable<StreamEvent> {
        const sendLoginCommand = () => this.streamingService.sendCommand({
                operation: AuthWebSocketOperation.LOGIN,
                data: [
                    this.authService.getAuthHeaderValue()
                ]
            } as AuthLoginCommand);

        const readLoginAnswer = (message: WebSocketMessage) => {
            let result: Observable<StreamEvent>;

            if (message.messageType === AuthWebSocketMessageType.LOGIN_SUCCESS) {

                console.log(`[${this.constructor.name}]`, 'Login Result:', message.data);

                this.authStateSubject$.next(true);

                result = of({
                    type: StreamEventType.READY,
                    payload: message.data
                } as StreamReadyEvent);
            } else {
                this.authStateSubject$.next(false);
                this.authService.innerLogout();

                result = EMPTY;
            }

            return result;
        };

        const authorize$ = this.streamingService.ready$.pipe(
            take(1),
            tap(sendLoginCommand),
            switchMap(() => this.authMessage$), // Not switchMapTo since authMessage$ could be not initialized.
            take(1),
            switchMap(readLoginAnswer)
        );

        return defer(() => this.isAuthorized
            ? of({type: StreamEventType.READY} as StreamReadyEvent)
            : authorize$
        ).pipe(
            share()
        );
    }

}
